import React, {FC, useMemo} from "react";
import {Select, message} from "antd";
import {useQuery, useMutation} from "react-query";
import keycloak from "../config/keycloak";
import api from "../config/axios";
import { useOrganisation } from "./OrganisationInformationProvider";


interface OrganisationSummary {
    id: number;
    name: string;
}

const hasAssumeOrganisationAccess = (): boolean => {
    const resourceAccess = keycloak.tokenParsed?.resource_access;
    if (!resourceAccess) {
        return false;
    }

    return Object.values(resourceAccess).some((resource: any) => {
        const roles = resource?.roles || [];
        return Array.isArray(roles) && roles.includes("assume-organisation-dashboard-access");
    });
};

export const OrganisationSwitcher: FC = () => {
    const currentOrganisationId = useOrganisation().id;
    const canAssumeOrganisation = useMemo(() => hasAssumeOrganisationAccess(), []);

    const {data: organisations = [], isLoading: organisationsLoading} = useQuery<OrganisationSummary[]>('/dashboard/organisations', {
        enabled: canAssumeOrganisation,
        staleTime: Infinity,
    });

    const {mutate: updateOrganisation, isLoading: isUpdatingOrganisation} = useMutation((organisationId: number) =>
        api.patch('/dashboard/organisation-admin/', {organisation: organisationId})
    , {
        onSuccess: () => {
            window.location.replace("/");
        },
        onError: () => {
            message.error('Unable to switch organisation. Please try again.');
        }
    });

    const shouldShowOrganisationSelect = canAssumeOrganisation && organisations.length > 0;

    if (!shouldShowOrganisationSelect) {
        return null;
    }

    return (
        <Select
            showSearch
            style={{width: '100%', padding: '5px'}}
            placeholder="Select organisation"
            value={currentOrganisationId}
            loading={organisationsLoading || isUpdatingOrganisation}
            onSelect={(selectedOrganisationId: number) => updateOrganisation(selectedOrganisationId)}
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            options={organisations.map((org) => ({label: org.name, value: org.id}))}
            disabled={isUpdatingOrganisation}
        />
    );
};
